"use client";
import { CONTENT_STATUS, NEWS_PER_PAGE_SIZE } from "@/constants";
import { LATEST_NEWS } from "@/queries/latestNews";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { generateLinkFromGeneralLink } from "@/lib/utils";
import {
  LatestNewsQuery,
  LatestNewsQueryVariables,
  UploadFile,
} from "@/types/generated";
import {
  GroupedNews,
  LIST_NEW_CATEGORY,
  NEWS_CATEGORY_MAPPING,
  NewsCategory,
} from "@/types/news";
import { formatDateToYYYYMMDD, getNewDetailPreviewImage } from "@/utils";
import React, { useState, useEffect } from "react";
import { CategoryTabs } from "./CategoryTab";
import { MonthGroup } from "./MonthGroupNew";
import { Skeleton } from "@/components/atoms/skelton";
import { fetchCMS } from "@/graphql/client/client";
import { useSearchParams, useRouter, usePathname } from "next/navigation";

interface NewsSectionProps {
  dataNewsInit: LatestNewsQuery;
  locale: string;
  defaultHotelId?: string;
}

/**
 * NewsPage Component
 *
 * Displays a categorized, paginated list of news grouped by month and year.
 * Includes category filtering, dynamic fetching, skeleton loading states, and pagination.
 */
const NewsPage: React.FC<NewsSectionProps> = ({
  dataNewsInit,
  locale,
  defaultHotelId,
}) => {
  const { japanAreas } = useJapanAreasStore();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const filterParam = searchParams.get("filter");

  // Determine initial tab based on URL filter parameter
  const getInitialTab = (): NewsCategory => {
    if (filterParam?.toLowerCase() === "restaurant") {
      return NewsCategory.RESTAURANTS;
    }
    if (filterParam?.toLowerCase() === "banquet") {
      return NewsCategory.BANQUETS;
    }
    if (filterParam?.toLowerCase() === "notice") {
      return NewsCategory.ANNOUNCEMENTS;
    }
    if (filterParam?.toLowerCase() === "stay") {
      return NewsCategory.ACCOMMODATION;
    }
    return NewsCategory.ALL;
  };

  const [newsData, setNewsData] = useState(dataNewsInit);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(
    (dataNewsInit.newsYearMonthDayItems?.length || 0) >= NEWS_PER_PAGE_SIZE,
  );
  const [activeTab, setActiveTab] = useState<NewsCategory>(getInitialTab());
  const [isInitialized, setIsInitialized] = useState(false);
  const listHotel = React.useMemo(
    () => japanAreas?.flatMap((area) => area.hotels) || [],
    [japanAreas],
  );

  const arr = [1, 2, 4, 5, 6, 7, 8];

  // Create dynamic filter criteria based on selected category and hotel ID
  const createFilterCriteria = (category: NewsCategory) => {
    const filter = {
      ...(defaultHotelId && {
        tags: {
          related_hotels: {
            containsi: defaultHotelId,
          },
        },
      }),
      display_on_hotel_page: {
        or: [
          { end_date: { eq: null } },
          { end_date: { gte: formatDateToYYYYMMDD(new Date()) } },
        ],
      },
    };

    if (category === NewsCategory.ALL) {
      return filter;
    }

    return {
      ...filter,
      properties: {
        category_type: {
          in: [category, NEWS_CATEGORY_MAPPING[category]],
        },
      },
    };
  };

  // Fetch paginated news data from CMS
  const fetchNewsData = async (
    page: number,
    category: NewsCategory,
  ): Promise<LatestNewsQuery> => {
    setIsLoading(true);

    try {
      const res = await fetchCMS<LatestNewsQuery>(LATEST_NEWS, {
        locale: locale,
        sort: ["properties.date_created:desc"],
        status: CONTENT_STATUS.PUBLISHED,
        pagination: {
          page,
          pageSize: NEWS_PER_PAGE_SIZE,
        },
        filters: createFilterCriteria(category),
      } as LatestNewsQueryVariables);

      return res;
    } catch (error) {
      console.error("Error fetching news data:", error);
      return { newsYearMonthDayItems: [] };
    }
  };

  // Handle category tab switch
  const handleTabChange = async (tab: NewsCategory) => {
    setActiveTab(tab);
    setCurrentPage(1);
    
    // Remove filter query parameter if switching away from restaurant, banquet, notice, or stay tabs
    if (
      tab !== NewsCategory.RESTAURANTS && 
      tab !== NewsCategory.BANQUETS && 
      tab !== NewsCategory.ANNOUNCEMENTS && 
      tab !== NewsCategory.ACCOMMODATION && 
      filterParam
    ) {
      router.replace(pathname);
    }
    
    const newData = await fetchNewsData(1, tab);
    setNewsData(newData);
    setHasMore(
      (newData.newsYearMonthDayItems?.length || 0) >= NEWS_PER_PAGE_SIZE,
    );
    setIsLoading(false);
  };

  // Handle pagination and load more results
  const handleLoadMore = async () => {
    if (isLoading || !hasMore) return;
    const nextPage = currentPage + 1;
    const newData = await fetchNewsData(nextPage, activeTab);
    if (newData.newsYearMonthDayItems?.length) {
      setNewsData((prevData) => ({
        ...prevData,
        newsYearMonthDayItems: [
          ...(prevData.newsYearMonthDayItems || []),
          ...(newData.newsYearMonthDayItems || []),
        ],
      }));
      setCurrentPage(nextPage);
      setHasMore(
        (newData.newsYearMonthDayItems?.length || 0) >= NEWS_PER_PAGE_SIZE,
      );
    } else {
      setHasMore(false);
    }
    setIsLoading(false);
  };

  // Effect to fetch restaurant, banquet, notice, or stay news if filter parameter is set
  useEffect(() => {
    const fetchInitialData = async () => {
      if (filterParam?.toLowerCase() === "restaurant" && !isInitialized) {
        setIsInitialized(true);
        setIsLoading(true);
        const newData = await fetchNewsData(1, NewsCategory.RESTAURANTS);
        setNewsData(newData);
        setHasMore(
          (newData.newsYearMonthDayItems?.length || 0) >= NEWS_PER_PAGE_SIZE,
        );
        setIsLoading(false);
      } else if (filterParam?.toLowerCase() === "banquet" && !isInitialized) {
        setIsInitialized(true);
        setIsLoading(true);
        const newData = await fetchNewsData(1, NewsCategory.BANQUETS);
        setNewsData(newData);
        setHasMore(
          (newData.newsYearMonthDayItems?.length || 0) >= NEWS_PER_PAGE_SIZE,
        );
        setIsLoading(false);
      } else if (filterParam?.toLowerCase() === "notice" && !isInitialized) {
        setIsInitialized(true);
        setIsLoading(true);
        const newData = await fetchNewsData(1, NewsCategory.ANNOUNCEMENTS);
        setNewsData(newData);
        setHasMore(
          (newData.newsYearMonthDayItems?.length || 0) >= NEWS_PER_PAGE_SIZE,
        );
        setIsLoading(false);
      } else if (filterParam?.toLowerCase() === "stay" && !isInitialized) {
        setIsInitialized(true);
        setIsLoading(true);
        const newData = await fetchNewsData(1, NewsCategory.ACCOMMODATION);
        setNewsData(newData);
        setHasMore(
          (newData.newsYearMonthDayItems?.length || 0) >= NEWS_PER_PAGE_SIZE,
        );
        setIsLoading(false);
      } else if (!isInitialized) {
        setIsInitialized(true);
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParam]);

  // Group news items by month and year for sectioned display
  const groupedNews = React.useMemo(() => {
    if (!newsData?.newsYearMonthDayItems?.length) return {};

    return newsData.newsYearMonthDayItems.reduce<GroupedNews>((acc, item) => {
      if (!item?.properties?.date_created || !item.properties.header_title) {
        return acc;
      }

      const date = new Date(item.properties.date_created);
      const year = date.getFullYear();
      const month = date.toLocaleString(locale, { month: "long" });
      const key = `${month} ${year}`;

      if (!acc[key]) acc[key] = [];

      // Extract hotel image based on relation or fallback to featured image
      const relatedHotelId = item?.tags?.related_hotels?.find(
        (item: { order: number }) => item?.order === 1,
      )?.documentId;

      const hotelImagesNew = getNewDetailPreviewImage(
        relatedHotelId,
        listHotel,
      );
      const imageFeatured = item?.properties.featured_image;
      const isShowFeatureImage = item?.properties?.use_featured_Image;
      const imageUrl = isShowFeatureImage
        ? imageFeatured || hotelImagesNew
        : hotelImagesNew;

      // Build grouped news item entry
      acc[key].push({
        createdAt: item.properties.date_created,
        properties: {
          header_title: item.properties.header_title,
          hotel_names: item.properties.hotel_names,
          featured_image: imageUrl as UploadFile,
          category_type: item.properties.category_type,
          date_created: item.properties.date_created,
          external_link: generateLinkFromGeneralLink(
            item?.properties?.external_link,
            locale,
          ),
          use_featured_Image: item.properties.use_featured_Image,
        },
        item_name: item.item_name,
      });

      return acc;
    }, {});
  }, [newsData, locale, listHotel]);

  return (
    <div className="w-full max-w-6xl px-6 py-4 md:px-8 md:py-8">
      {/* Category tabs and news list */}
      {!isLoading && (
        <>
          <CategoryTabs
            categories={LIST_NEW_CATEGORY}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          <div className="py-4">
            {Object.keys(groupedNews)?.length > 0 ? (
              <div>
                {Object.entries(groupedNews).map(([monthYear, items]) => (
                  <MonthGroup
                    key={monthYear}
                    monthYear={monthYear}
                    items={items}
                    locale={locale}
                  />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-gray-500">
                ニュースが見つかりませんでした。
              </div>
            )}
          </div>
        </>
      )}

      {/* Skeleton loading state */}
      {isLoading && (
        <div>
          <div className="mb-4 flex items-center justify-items-start gap-2 overflow-x-auto text-[14px] font-[var(--font-bold)] md:text-[20px]">
            <Skeleton className="min-h-14 w-30" />
            <Skeleton className="min-h-14 w-30" />
            <Skeleton className="min-h-14 w-30" />
            <Skeleton className="min-h-14 w-30" />
          </div>
          <div className="mb-2 w-full border-b border-gray-200">
            <Skeleton className="min-h-14 w-30" />
          </div>
          {arr.map((item, index) => {
            const newsPageKey = `news-page-item-${index}`;
            return (
              <div
                key={newsPageKey}
                className="border-b border-b-[var(--stroke-gray-normal)] py-6 md:py-8"
              >
                <div className="flex min-h-[173px] flex-row items-start">
                  <div className="flex w-2/3 flex-col md:w-5/6 md:flex-row md:items-center md:pt-4">
                    <div className="flex md:w-1/10 md:pr-6">
                      <div className="text-2xl font-[var(--font-bold)] md:text-3xl">
                        <Skeleton className="h-10 w-18" />
                      </div>
                    </div>
                    <div className="flex-grow pt-2 pr-4 md:w-9/10 md:border-l md:border-l-[var(--stroke-gray-normal)] md:pt-0 md:pr-22 md:pl-8">
                      <h2 className="text-sm font-[var(--font-bold)] md:text-2xl">
                        <Skeleton className="h-20 w-full" />
                      </h2>
                    </div>
                  </div>
                  <div className="w-1/3 md:w-3/10">
                    <Skeleton className="h-[174px] w-[282px] rounded" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load more button */}
      {!isLoading && hasMore && (
        <div className="mt-8 mb-16 flex justify-center">
          <button
            onClick={handleLoadMore}
            className="flex h-26 w-26 cursor-pointer items-center justify-center rounded-full border"
          >
            <span className="text-sm font-[var(--font-semibold)]">
              もっと見る
            </span>
          </button>
        </div>
      )}

      {/* Skeleton for load more button while fetching */}
      {isLoading && (
        <div className="mt-8 mb-16 flex items-center justify-center">
          <Skeleton className="h-26 w-26 rounded-full" />
        </div>
      )}
    </div>
  );
};

export default NewsPage;
